import { defineStore } from 'pinia';
import companyApi from '@/api/modules/companyApi';
import { HttpError } from '@/api/apiClient';
import type { CompanyReceitaBaiscData, CompanyRevenuePrediction } from '@/types/company';
import { normalizeCnpj } from '@/utils/formatters';

// --- Interfaces matching Backend Schemas ---

interface NetworkSummary {
    total_branches: number;
    active_branches: number;
    state_distribution: Array<{ state: string; count: number }>;
    timeline_openings: Array<{ year: number; opened: number }>;
    cnae_distribution: Array<{ code: string; description: string | null; count: number }>;
    hq_main_cnae: string | null;
    hq_main_cnae_desc: string | null;
    hq_secondary_cnaes: Array<{ code: string; description: string | null }>;
}

export interface BranchSummary {
    cnpj14: string;
    nome_fantasia: string | null;
    municipio: string | null;
    uf: string | null;
    situacao_cadastral_code: string | null;
    is_main: boolean;
    cnae_principal_code: string | null;
    data_de_inscricao?: string | null;
}

export interface BranchFilters {
    search?: string;
    uf?: string;
    situacao?: string;
    cnae?: string;
}

export interface CompanySearchResult {
    name: string;
    tax_id: string;
    address: string;
    phone: string;
}

interface BranchDetail {
    cnpj14: string;
    nome: string;
    nome_fantasia: string | null;
    is_main: boolean;
    situacao_cadastral_code: string;
    situacao_cadastral_data: string | null;
    motivo_situacao_code: string | null;
    motivo_situacao_text: string | null;
    data_de_inscricao: string | null;
    capital_social: number | null;
    porte_code: string | null;
    porte_text: string | null;
    cnae_principal_code: string;
    cnae_principal_text: string | null;
    cnae_secundario_code: string[];
    cnae_secundarias?: Array<{ code: string; text: string | null }>; // Added to match backend
    natureza_juridica_code: string | null;
    natureza_juridica_text: string | null;
    situacao_especial_text: string | null; // Added
    endereco: {
        tipo_logradouro: string | null;
        logradouro: string | null;
        numero: string | null;
        complemento: string | null;
        bairro: string | null;
        cep: string | null;
        uf: string | null;
        municipio_code: string | null;
        municipio_text: string | null; // Added
        nome_pais: string | null; // Added
    };
    contatos: {
        email: string | null;
        telefone: Array<{ ddd: string; number: string }>;
        fax: { ddd: string; number: string };
    };
    simples: {
        is_simples: boolean;
        data_opcao: string | null;
        data_exclusao: string | null;
    };
    opc_simples_text: string | null; // Added
    opc_mei_text: string | null; // Added
    qsa?: any[];
}

// --- Component-Facing Aggregation Interfaces (Mapped) ---

export interface CorporateSummary {
    totalBranches: number;
    activeCount: number;
    statesCount: number;

    // HQ Activity
    mainActivity: string;
    mainActivityDescription?: string | null;
    secondaryActivities: Array<{ code: string; description: string | null }>;
}

export interface MapData {
    [state: string]: number;
}

export interface TimelineYear {
    year: string;
    opened: number;
    closed: number;
    activeTotal: number;
}

export interface CnaeAggregation {
    code: string;
    count: number;
    description?: string;
}

// QSA Aggregation (Still needed for QsaWidget, mapped from generalData)
export interface QsaAggregation {
    taxId: string;
    name: string;
    primaryRole: string;
    branchCount: number;
    ageGroup: string | null;
    relationships: any[];
}

// 'pending' is the state before the tab's first fetch starts: the card shows the spinner, not
// an empty state.
type RevenuePredictionStatus = 'pending' | 'loading' | 'ready' | 'failed';

interface CompanyState {
  generalData: CompanyReceitaBaiscData | null;
  generalDataError: string | null;
  companyNotFound: boolean;
  hasGeneralData: boolean | null;
  requestingGeneralData: boolean;
  currentCnpj8: string | null;

  // --- Network State ---
  networkStats: NetworkSummary | null;
  branchesList: BranchSummary[];
  branchesTotal: number;
  branchesPage: number;
  branchesLimit: number;
  branchesFilters: BranchFilters;

  // --- Detail State ---
  selectedBranchData: BranchDetail | null;
  selectedBranchCnpj: string | null;
  branchesLoading: boolean;
  branchDetailLoading: boolean;

  // --- DUNS Number State ---
  dunsNumber: string | null;

  // --- Revenue Prediction State ---
  revenuePrediction: CompanyRevenuePrediction | null;
  revenuePredictionCnpj8: string | null; // the CNPJ root the prediction above belongs to
  revenuePredictionStatus: RevenuePredictionStatus;
}

export const useCompanyStore = defineStore('company', {
  state: (): CompanyState => ({
    generalData: null,
    generalDataError: null,
    companyNotFound: false,
    hasGeneralData: null,
    requestingGeneralData: false,
    currentCnpj8: null,

    networkStats: null,
    branchesList: [],
    branchesTotal: 0,
    branchesPage: 1,
    branchesLimit: 50,
    branchesFilters: {},

    selectedBranchData: null,
    selectedBranchCnpj: null,
    branchesLoading: false,
    branchDetailLoading: false,

    dunsNumber: null,

    revenuePrediction: null,
    revenuePredictionCnpj8: null,
    revenuePredictionStatus: 'pending',
  }),

  getters: {
      cnpj14: (state): string | null => state.generalData?.cnpj ?? null,

      // Map Backend 'NetworkSummary' + 'GeneralData' -> Frontend 'CorporateSummary'
      corporateSummary: (state): CorporateSummary | null => {
          if (!state.generalData || !state.networkStats) return null;

          const g = state.generalData;
          const n = state.networkStats;

          // Resolve Secondary Activities
          let secondary: Array<{ code: string; description: string | null }> = [];
          if (n.hq_secondary_cnaes && n.hq_secondary_cnaes.length > 0) {
              secondary = n.hq_secondary_cnaes;
          } else if (g.cnae_secundarias) {
              secondary = g.cnae_secundarias.map(s => ({
                  code: s.cnae_secundario,
                  description: s.cnae_text
              }));
          }

          return {
              totalBranches: n.total_branches,
              activeCount: n.active_branches,
              statesCount: n.state_distribution.length,

              // Activity from Network Stats (Enriched HQ activity)
              mainActivity: n.hq_main_cnae || g.cnae_fiscal,
              mainActivityDescription: n.hq_main_cnae_desc || g.cnae_principal_text,
              secondaryActivities: secondary
          };
      },

      mapData: (state): MapData => {
          if (!state.networkStats) return {};
          const map: MapData = {};
          state.networkStats.state_distribution.forEach(d => {
              map[d.state] = d.count;
          });
          return map;
      },

      timelineData: (state): TimelineYear[] => {
          if (!state.networkStats) return [];

          // Sort by year to ensure correct cumulative calculation
          const sorted = [...state.networkStats.timeline_openings].sort((a, b) => a.year - b.year);

          let runningTotal = 0;

          return sorted.map(d => {
              runningTotal += d.opened;
              return {
                  year: d.year.toString(),
                  opened: d.opened,
                  closed: 0, // Not provided by backend yet
                  activeTotal: runningTotal
              };
          });
      },

      hasMoreBranches: (state): boolean => state.branchesList.length < state.branchesTotal,

      aggregatedCnaes: (state): CnaeAggregation[] => {
          if (!state.networkStats) return [];
          return state.networkStats.cnae_distribution.map(d => ({
              code: d.code,
              count: d.count,
              description: d.description || undefined
          }));
      },

      // QSA is company-wide, so we pull from General Data
      aggregatedQsa: (state): QsaAggregation[] => {
          if (!state.generalData || !state.generalData.socios) return [];

          return state.generalData.socios.map((s: any) => ({
              taxId: s.cnpj_cpf_socio,
              name: s.nome_socio,
              primaryRole: s.text_qualificacao, // Use text if available
              branchCount: 1, // Logic not applicable for simplified view
              ageGroup: s.age_group_text,
              relationships: [] // Detail not needed for list
          }));
      },

      qsaDemographics: (state): Record<string, number> => {
          // Calculate if age groups available
           const demo: Record<string, number> = {};
           if (state.generalData?.socios) {
               state.generalData.socios.forEach((s: any) => {
                   if (s.age_group_text) {
                       demo[s.age_group_text] = (demo[s.age_group_text] || 0) + 1;
                   }
               });
           }
           return demo;
      }
  },

  actions: {
    // --- Existing Actions ---
    async searchCompanies(searchTerm: string): Promise<CompanySearchResult[]> {
       // ... (Keep existing implementation logic if needed, or rely on API)
       // This was a mock in previous store, effectively replacing with API call
       const res = await companyApi.getCompanyByCnpjOrName(searchTerm, 0, 10, false, 'name');
       return res.data.map((c: any) => ({
           name: c.razao_social,
           tax_id: c.tax_id_full,
           address: '',
           phone: ''
       }));
    },

    resetGeneralData(): void {
      this.generalData = null;
      this.companyNotFound = false;
      this.hasGeneralData = null;
      this.generalDataError = null;
      this.requestingGeneralData = false;
      this.currentCnpj8 = null;
      this.networkStats = null;
      this.branchesList = [];
      this.selectedBranchData = null;
      this.dunsNumber = null;
      this.revenuePrediction = null;
      this.revenuePredictionCnpj8 = null;
      this.revenuePredictionStatus = 'pending';
    },

    // Single round trip: the backend checks access, auto-acquires (and bills) the data
    // if the account doesn't have it yet, and returns it either way.
    async ensureGeneralData(cnpj: string): Promise<void> {
      this.companyNotFound = false;
      this.generalDataError = null;
      this.requestingGeneralData = true;

      try {
        const response = await companyApi.ensureGeneralData(cnpj);
        let payload = response?.data ?? response;
        if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'data')) {
          payload = payload.data;
        }

        this.generalData = payload || null;
        this.hasGeneralData = !!payload;
        this.currentCnpj8 = cnpj;
      } catch (error: any) {
        this.companyNotFound = error instanceof HttpError && error.status === 404;
        console.error('Error ensuring general data:', error);
        this.generalData = null;
        this.hasGeneralData = false;
        this.currentCnpj8 = cnpj;
        // App-authored message only — never surface the raw HTTP body (error.message here is
        // literally `HTTP <status>: <response text>`, meant for logs, not end users.
        this.generalDataError = 'Não foi possível carregar os dados da Receita.';
      } finally {
        this.requestingGeneralData = false;
      }
    },

    // Predicted annual revenue band. Free model data, so there is no access check first.
    async fetchRevenuePrediction(cnpj8: string): Promise<void> {
      if (this.revenuePredictionCnpj8 === cnpj8) return;

      this.revenuePredictionStatus = 'loading';
      try {
        this.revenuePrediction = await companyApi.getRevenuePrediction(cnpj8);
        this.revenuePredictionCnpj8 = cnpj8;
        this.revenuePredictionStatus = 'ready';
      } catch (error) {
        // The band is secondary info on the tab: on failure the card just says it is unavailable.
        console.error('Error fetching company revenue prediction:', error);
        this.revenuePrediction = null;
        this.revenuePredictionStatus = 'failed';
      }
    },

    // --- New Network Actions ---

    async fetchNetworkSummary(cnpj8: string) {
        try {
            const data = await companyApi.getNetworkSummary(cnpj8);
            this.networkStats = data;
        } catch (e) {
            console.error("Failed to fetch network summary", e);
        }
    },

    async fetchBranchesList(cnpj8: string, page = 1, limit = 0, filters: BranchFilters = {}) {
        limit = limit || this.branchesLimit;
        this.branchesLoading = true;
        try {
            // A page-1 fetch establishes a new result set + filter context;
            // subsequent pages must reuse the same filters to stay consistent.
            if (page === 1) {
                this.branchesFilters = { ...filters };
            }
            const res = await companyApi.getBranchesList(cnpj8, page, limit, this.branchesFilters);
            if (page === 1) {
                this.branchesList = res.items;
            } else {
                this.branchesList = [...this.branchesList, ...res.items];
            }
            this.branchesTotal = res.total;
            this.branchesPage = page;
            this.branchesLimit = limit;
        } catch (e) {
            console.error("Failed to fetch branches list", e);
        } finally {
            this.branchesLoading = false;
        }
    },

    /** Re-run the branch query from page 1 with a new filter set. */
    async applyBranchFilters(cnpj8: string, filters: BranchFilters = {}) {
        await this.fetchBranchesList(cnpj8, 1, this.branchesLimit, filters);
    },

    async loadMoreBranches(cnpj8: string) {
        if (this.branchesLoading || this.branchesList.length >= this.branchesTotal) return;
        await this.fetchBranchesList(cnpj8, this.branchesPage + 1, this.branchesLimit, this.branchesFilters);
    },

    async fetchBranchDetail(cnpj14: string) {
        // Separate from branchesLoading so paging the list (load-more) never
        // flips the detail pane into a loading state.
        this.branchDetailLoading = true;
        try {
            const data = await companyApi.getBranchDetail(cnpj14);
            // Ensure nested objects exist even if null in DB to prevent UI crash
            if (!data.contatos) data.contatos = { telefone: [] };
            if (!data.endereco) data.endereco = {};

            this.selectedBranchData = data;
            this.selectedBranchCnpj = cnpj14;
        } catch (e) {
            console.error("Failed to fetch branch detail", e);
            this.selectedBranchData = null;
        } finally {
            this.branchDetailLoading = false;
        }
    },

    clearSelectedBranch() {
        this.selectedBranchData = null;
        this.selectedBranchCnpj = null;
    },

    // --- DUNS Number Action ---
    async fetchDunsNumber(cnpj14: string): Promise<void> {
        try {
            // Ensure CNPJ 14 is clean (14 characters)
            const cleanCnpj14 = normalizeCnpj(cnpj14);
            if (cleanCnpj14.length !== 14) {
                this.dunsNumber = null;
                return;
            }

            const response = await companyApi.getDunsNumber(cleanCnpj14);
            this.dunsNumber = response.duns_number || null;
        } catch (error) {
            // Handle errors gracefully - set to null but don't block page load
            // Backend will raise errors if DUNS can't be retrieved, but we handle it silently on frontend
            console.error('Error fetching DUNS number:', error);
            this.dunsNumber = null;
        }
    }
  },
});
